import { useRef, useEffect, useMemo, DependencyList, EffectCallback } from 'react';
import isEqual from 'react-fast-compare';

export const useHasUpdated = <T>(firstValuesToCompare: T, SecondValuesToCompare: T): boolean =>
  useMemo(() => !isEqual(firstValuesToCompare, SecondValuesToCompare), [firstValuesToCompare, SecondValuesToCompare]);

export const useDidMount = (callback: () => void, deps: unknown[] = []): void => {
  const firstRender = useRef(true);

  useEffect(() => {
    if (firstRender?.current) {
      firstRender.current = false;
      return;
    }
    callback?.();
    // biome-ignore lint/correctness/useExhaustiveDependencies: exception
  }, deps);
};

export const useDeepCompareWithPrevious = <T>(value?: T): [boolean, T | undefined] => {
  const prevVal = usePrevious(value);
  return [!isEqual(prevVal, value), prevVal];
};

export const useDeepCompare = <T>(value: T): boolean => {
  const prevVal = usePrevious(value);
  return !isEqual(prevVal, value);
};

export const useCompare = <T>(value: T): boolean => {
  const prevVal = usePrevious(value);
  return prevVal !== value;
};

export const usePrevious = <T>(value: T): T | undefined => {
  const ref = useRef<T>();

  useEffect(() => {
    ref.current = value;
  });

  return ref.current;
};

export const useDeepMemo = <T>(memoFn: () => T, key: unknown[]): T => {
  const ref = useRef<{ key: unknown[]; value: T }>();
  if (!ref.current || !isEqual(key, ref.current.key)) {
    ref.current = { key, value: memoFn() };
  }

  return ref.current.value;
};

export const useDeepCompareEffect = (
  effect: EffectCallback,
  depsToDeepCompare: DependencyList,
  otherDeps?: DependencyList
) => {
  const hasChanged = useDeepCompare(depsToDeepCompare);
  const deps = [...(otherDeps ?? EMPTY_DEP_LIST)];

  // biome-ignore lint/correctness/useExhaustiveDependencies: exception
  useEffect(() => {
    let e: ReturnType<EffectCallback>;
    if (hasChanged) {
      e = effect();
    }
    return () => {
      e?.();
    };
  }, [hasChanged, ...deps]);
};

const EMPTY_DEP_LIST = [] as DependencyList;
